import { Avatar } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

const styles = {
    avatar: {
        border: "1px solid #ddd",
        boxShadow: "0 1px 5px 2px rgba(0, 0, 0, 0.23)",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        cursor: "pointer",
    },
};

export default function AvatarWithFallback({
    src,
    alt = "",
    size = 96,
    defaultImage = "",
    fallbackSrc = "",
    lazy = true,
    lazyRootMargin = "200px",
    ...props
}) {
    const [imgSrc, setImgSrc] = useState(src);
    const [hasError, setHasError] = useState(false);
    const resolvedFallback = defaultImage || fallbackSrc;
    const wrapperRef = useRef(null);
    const [isVisible, setIsVisible] = useState(!lazy);

    const initials = useMemo(() => {
        if (!alt) return "";
        return alt
            .split(" ")
            .filter(Boolean)
            .map((word) => word[0])
            .slice(0, 2)
            .join("")
            .toUpperCase();
    }, [alt]);

    const handleError = () => {
        if (hasError) return; // already fell back once, stop here

        setHasError(true);

        if (resolvedFallback) {
            setImgSrc(resolvedFallback);
        } else {
            setImgSrc("");
        }
    };

    useEffect(() => {
        if (!lazy) return;
        const node = wrapperRef.current;
        if (!node) return;

        if (!("IntersectionObserver" in window)) {
            setIsVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: lazyRootMargin }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [lazy, lazyRootMargin]);

    useEffect(() => {
        if (!isVisible) return;
        if (src) {
            setImgSrc(src);
            setHasError(false);
        } else {
            setImgSrc(resolvedFallback || "");
        }
    }, [src, resolvedFallback, isVisible]);

    return (
        <span ref={wrapperRef}>
            <Avatar
                {...props}
                size={size}
                src={isVisible ? (imgSrc || undefined) : undefined}
                alt={alt || "Sponsor"}
                style={{ ...styles.avatar, ...props?.style, height: size, width: size || {} }}
                onError={handleError}
            >
                {initials || "?"}
            </Avatar>
        </span>
    );
}

AvatarWithFallback.propTypes = {
    src: PropTypes.string,
    alt: PropTypes.string,
    size: PropTypes.number,
    defaultImage: PropTypes.string,
    fallbackSrc: PropTypes.string,
    lazy: PropTypes.bool,
    lazyRootMargin: PropTypes.string,
};
